import PageHeader from '@/components/common/PageHeader';
import { Button, Form, Modal } from 'antd';
import { exportToCSV, generateCSVFilename, formatDateForCSV } from '@/utils/csv';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { IoCloudUploadOutline, IoPersonAddOutline } from 'react-icons/io5';
import {
  BulkImportModal,
  OnboardingFilters,
  OnboardingTable,
  StatisticsCards,
  VSSetupFormModal,
  WelcomeEmailModal,
} from '../components';
import { IOnboardingAccount, IOnboardingForm } from '../types';
import {
  useOnboardingAccountList,
  useCreateOnboardingAccount,
  useUpdateOnboardingContact,
  useAllAccountCountsByStatus,
  useAccountCountByStatus,
  useExportOnboardingAccounts,
  useSendOnboardingReminder,
  useDeactivateOnboardingAccount,
  useActivateOnboardingAccount,
} from '../hooks';
import { ICommonQueryParams } from '@/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_STARTING_PAGE, CUSTOMER_TYPE_TRADE_COUNTER, CREDIT_TERMS_UPFRONT, DEFAULT_QUOTE_VALIDITY_DAYS, AccountStatusEnum } from '../constants';
import { ROUTESPATH } from '@/routes/constant';
import { buildOnboardingFilterQuery, generateRegionFilterQuery } from '../utils';
import { useGetCurrentUser } from '@/hooks/app';
import { PermissionChecker } from '@/utils/permission';
import { REGION_ALL } from '@/utils/constant/appConstant';


export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();

  // Form instance
  const [form] = Form.useForm<IOnboardingForm>();

  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canCreateOnboarding = permissionChecker.canCreateOnboarding();
  const canUpdateUserOnboarding = permissionChecker.canUpdateUserOnboarding();

  // Region filter logic
  const userRegion = currentUserData?.currentUser?.dogma_region;
  const isRegionAll = Boolean(userRegion && userRegion === REGION_ALL);
  const region = !isRegionAll && userRegion ? userRegion : undefined;

  // State
  const [queryParams, setQueryParams] = useState<ICommonQueryParams>({
    current: DEFAULT_STARTING_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    filter: '',
  });

  const [showVSSetupForm, setShowVSSetupForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showWelcomeEmail, setShowWelcomeEmail] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<number | null>(null);
  const [currentRegion, setCurrentRegion] = useState<number | null>(null);
  const [currentSearchText, setCurrentSearchText] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderAccount, setReminderAccount] = useState<IOnboardingAccount | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateAccount, setDeactivateAccount] = useState<IOnboardingAccount | null>(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateAccount, setActivateAccount] = useState<IOnboardingAccount | null>(null);

  // Set default region filter for users without "all regions" access
  useEffect(() => {
    if (userRegion && !isRegionAll && currentRegion === null) {
      setCurrentRegion(userRegion);
    }
  }, [userRegion, isRegionAll, currentRegion]);

  const defaultRegionFilters = isRegionAll ? "" : generateRegionFilterQuery(userRegion);

  // Hooks
  const { data: accountsResponse, isLoading } = useOnboardingAccountList({
    queryParams: {
      ...queryParams,
      filter: queryParams.filter ? queryParams.filter : (defaultRegionFilters ? `&$filter=${defaultRegionFilters}` : '')
    }
  });
  const exportOnboardingMutation = useExportOnboardingAccounts();
  const createAccountMutation = useCreateOnboardingAccount();
  const updateContactMutation = useUpdateOnboardingContact();
  const sendReminderMutation = useSendOnboardingReminder();
  const deactivateAccountMutation = useDeactivateOnboardingAccount();
  const activateAccountMutation = useActivateOnboardingAccount();
  const { data: statusCountsData } = useAllAccountCountsByStatus(region);
  const { data: totalCountData } = useAccountCountByStatus(undefined, region);

  const accounts = accountsResponse?.value || [];
  const totalCount = totalCountData?.value?.[0]?.count ?? 0;

  const handleTableChange = (current: number, pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, current, pageSize }));
  };

  const handleExportCSV = () => {
    exportOnboardingMutation.mutate(queryParams.filter || '', {
      onSuccess: (data: any) => {
        const rows = data?.value || [];
        const csvData = rows.map((a: any) => ({
          'Company Name': a.name || '-',
          'Trading Name': a.dogma_tradingname || '-',
          'Contact Name': a.primarycontactid?.fullname || '-',
          'Contact Email': a.primarycontactid?.emailaddress1 || '-',
          'Contact Phone': a.primarycontactid?.telephone1 || '-',
          'Region': a['dogma_region@OData.Community.Display.V1.FormattedValue'] || '-',
          'Status': a['dogma_onboardingstatus@OData.Community.Display.V1.FormattedValue'] || '-',
          'Created Date': a.createdon ? formatDateForCSV(a.createdon) : '-',
        }));
        const columns = [
          { key: 'Company Name' as const, header: 'Company Name' },
          { key: 'Trading Name' as const, header: 'Trading Name' },
          { key: 'Contact Name' as const, header: 'Contact Name' },
          { key: 'Contact Email' as const, header: 'Contact Email' },
          { key: 'Contact Phone' as const, header: 'Contact Phone' },
          { key: 'Region' as const, header: 'Region' },
          { key: 'Status' as const, header: 'Status' },
          { key: 'Created Date' as const, header: 'Created Date' },
        ];
        exportToCSV(csvData, columns, generateCSVFilename('onboarding'));
      },
    });
  };

  // Handle search text change
  const handleSearchTextChange = (searchText: string) => {
    setCurrentSearchText(searchText);

    const filter = buildOnboardingFilterQuery({ searchText, status: currentStatus, region: currentRegion });
    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter,
    });
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: number | null) => {
    setCurrentStatus(status);
    const filter = buildOnboardingFilterQuery({ searchText: currentSearchText, status, region: currentRegion });
    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter,
    });
  };

  const handleApplyAllFilters = ({ status, region }: { status: number | null; region: number | null }) => {
    setCurrentStatus(status);
    setCurrentRegion(region);

    const filter = buildOnboardingFilterQuery({ searchText: currentSearchText, status, region });
    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter,
    });
  };

  // Handle region filter change
  const handleRegionFilterChange = (region: number | null) => {
    setCurrentRegion(region);

    const filter = buildOnboardingFilterQuery({ searchText: currentSearchText, status: currentStatus, region });
    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter,
    });
  };



  // Handle VS Setup Form Submit
  const handleVSSetupSubmit = async () => {
    try {
      const values = await form.validateFields();
      const result = await createAccountMutation.mutateAsync(values as IOnboardingForm);
      setShowVSSetupForm(false);
      form.resetFields();
      if (result?.accountId) {
        navigate(ROUTESPATH.CUSTOMER_DISCOUNT.replace(':accountId', result.accountId));
      }
    } catch (error) {
      if (error instanceof Error) {
      } else {
      }
    }
  };

  // Handle send reminder - open confirmation modal
  const handleSendReminder = (account: IOnboardingAccount) => {
    setReminderAccount(account);
    setShowReminderModal(true);
  };

  // Confirmed - hit the API
  const handleConfirmReminder = () => {
    if (!reminderAccount) return;
    const contactName = reminderAccount.primarycontactid?.fullname || 'Customer';
    const email = reminderAccount.primarycontactid?.emailaddress1 || reminderAccount.emailaddress1;

    sendReminderMutation.mutate(
      {
        accountIds: [reminderAccount.accountid],
        subject: 'Reminder: Complete Your Victorian Sliders CPQ Portal Setup',
        body: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <p>Dear ${contactName},</p>

            <p>This is a friendly reminder that your Victorian Sliders CPQ Portal account has been created but the setup has not been completed yet.</p>

            <p><strong>Login Details:</strong><br/>
            Portal URL: <a href="https://portal.victoriansliders.co.uk">https://portal.victoriansliders.co.uk</a><br/>
            Username: ${email}<br/>
            Temporary Password: (Will be sent separately)</p>

            <p><strong>Next Steps:</strong></p>
            <ol>
              <li>Log in to the portal using the credentials above</li>
              <li>Change your password</li>
              <li>Complete the setup wizard to configure:
                <ul>
                  <li>Your company branding (logo, colors)</li>
                  <li>Your pricing margins</li>
                  <li>Your terms and conditions</li>
                </ul>
              </li>
            </ol>

            <p>Completing your setup will enable you to access all the features of the CPQ Portal.</p>

            <p>Support: <a href="mailto:support@victoriansliders.co.uk">support@victoriansliders.co.uk</a> | +44 20 7946 0000</p>

            <p>Best regards,<br/>Victorian Sliders Team</p>
          </div>
        `,
        isHtml: true,
      },
      {
        onSettled: () => {
          setShowReminderModal(false);
          setReminderAccount(null);
        },
      }
    );
  };

  // Handle edit onboarding
  const handleEditOnboarding = (account: IOnboardingAccount) => {
    setSelectedAccountId(account.accountid);
    setSelectedContactId(account.primarycontactid?.contactid || null);
    setIsEditMode(true);
    form.setFieldsValue({
      "Company Name": account?.name,
      "Trading Name": account?.dogma_tradingname,
      "Prefix": account?.dogma_prefix || "",
      "Primary Contact Name": account?.primarycontactid?.fullname, // Contact name is not in account
      "Primary Contact Email": account?.primarycontactid?.emailaddress1,
      "Primary Contact Phone": account?.primarycontactid?.telephone1 ?? "",
      "Company Address": account?.address1_composite,
      "Region": account?.dogma_region,
      "Customer Type": account?.customertypecode,
      "Credit Terms": account?.paymenttermscode,
      "Quote Validity (days)": account?.dogma_quotevaliditydays,
      "Assigned RSM": account?._dogma_assignedrsm_value,
      "Currency": account?._dogma_dealerscurrency_value,
    });
    setShowVSSetupForm(true);
  };

  // Handle update onboarding
  const handleUpdateOnboarding = async () => {
    if (!selectedAccountId) return;

    try {
      const values = await form.validateFields();

      // Update everything through the contact endpoint with all fields
      await updateContactMutation.mutateAsync({
        accountId: selectedAccountId,
        contactId: selectedContactId || undefined,
        formValues: values as Partial<IOnboardingForm>,
      });

      setShowVSSetupForm(false);
      setIsEditMode(false);
      setSelectedAccountId(null);
      setSelectedContactId(null);
      form.resetFields();
    } catch (error) {
      if (error instanceof Error) {
      } else {
        // Validation error - Ant Design will show inline errors
      }
    }
  };

  const handleCancelForm = () => {
    setShowVSSetupForm(false);
    setIsEditMode(false);
    setSelectedAccountId(null);
    setSelectedContactId(null);
    form.resetFields();
  };

  // Handle deactivate account - open confirmation modal
  const handleDeactivateAccount = (account: IOnboardingAccount) => {
    setDeactivateAccount(account);
    setShowDeactivateModal(true);
  };

  // Confirmed deactivation - hit the API
  const handleConfirmDeactivate = () => {
    if (!deactivateAccount) return;
    const deactivationState = JSON.stringify({
      statecode: deactivateAccount.statecode,
      statuscode: deactivateAccount.statuscode,
    });
    deactivateAccountMutation.mutate(
      { id: deactivateAccount.accountid, statecode: 1, statuscode: 2, dogma_deactivationstate: deactivationState },
      {
        onSettled: () => {
          setShowDeactivateModal(false);
          setDeactivateAccount(null);
        },
      }
    );
  };

  // Handle activate account - open confirmation modal
  const handleActivateAccount = (account: IOnboardingAccount) => {
    setActivateAccount(account);
    setShowActivateModal(true);
  };

  // Confirmed activation - restore statecode/statuscode from dogma_deactivationstate and clear the field
  const handleConfirmActivate = () => {
    if (!activateAccount) return;
    let statecode = 0;
    let statuscode = AccountStatusEnum.ACCOUNT_CREATED;
    if (activateAccount.dogma_deactivationstate) {
      try {
        const saved = JSON.parse(activateAccount.dogma_deactivationstate);
        if (saved.statecode !== undefined) statecode = saved.statecode;
        if (saved.statuscode !== undefined) statuscode = saved.statuscode;
      } catch {
        // fall back to defaults if parse fails
      }
    }
    activateAccountMutation.mutate(
      { id: activateAccount.accountid, statecode, statuscode, dogma_deactivationstate: null },
      {
        onSettled: () => {
          setShowActivateModal(false);
          setActivateAccount(null);
        },
      }
    );
  };

  // Navigate to discount configuration page for the selected account
  const handleConfigureDiscount = (account: IOnboardingAccount) => {
    navigate(
      ROUTESPATH.CUSTOMER_DISCOUNT.replace(':accountId', account.accountid),
      { state: { account } }
    );
  };

  // Handle opening new form
  const handleOpenNewForm = () => {
    form.setFieldsValue({
      "Company Name": '',
      "Trading Name": '',
      "Prefix": "",
      "Primary Contact Name": '',
      "Primary Contact Email": '',
      "Primary Contact Phone": '',
      "Company Address": '',
      "Region": undefined,
      "Customer Type": CUSTOMER_TYPE_TRADE_COUNTER,
      "Credit Terms": CREDIT_TERMS_UPFRONT,
      "Quote Validity (days)": DEFAULT_QUOTE_VALIDITY_DAYS,
    });
    setShowVSSetupForm(true);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      <PageHeader
        title="Customer Onboarding"
        subtitle="Onboard new trade counters and installers"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Onboarding' },
        ]}
        actions={
          <>
            <Button
              icon={<IoCloudDownloadOutline />}
              loading={exportOnboardingMutation.isPending}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
            {canCreateOnboarding && (
              <>
                <Button
                  icon={<IoCloudUploadOutline />}
                  onClick={() => setShowBulkImport(true)}
                >
                  Bulk Import
                </Button>
                <Button
                  type="primary"
                  icon={<IoPersonAddOutline />}
                  onClick={handleOpenNewForm}
                >
                  Onboard New Customer
                </Button>
              </>
            )}
          </>
        }
      />

      {/* Statistics Cards */}
      <StatisticsCards
        statusCounts={statusCountsData?.value || []}
        totalCount={totalCount}
        loading={isLoading}
      />

      {/* Filters */}
      <OnboardingFilters
        filterStatus={currentStatus}
        filterRegion={currentRegion}
        searchQuery={currentSearchText}
        onStatusChange={handleStatusFilterChange}
        onRegionChange={handleRegionFilterChange}
        onSearchChange={handleSearchTextChange}
        onApplyFilters={handleApplyAllFilters}
        totalRecords={accounts.length}
        filteredCount={accounts.length}
        statusCounts={statusCountsData?.value || []}
        isRegionAll={isRegionAll}
        userRegion={userRegion}
      />

      {/* Onboarding Records Table */}
      <OnboardingTable
        records={accounts}
        onEdit={handleEditOnboarding}
        onViewEmail={(account) => {
          setSelectedAccountId(account.accountid);
          setShowWelcomeEmail(true);
        }}
        onSendReminder={handleSendReminder}
        onDeactivate={handleDeactivateAccount}
        onActivate={handleActivateAccount}
        onConfigureDiscount={handleConfigureDiscount}
        isLoading={isLoading}
        canUpdateOnboarding={canUpdateUserOnboarding}
        current={queryParams.current}
        pageSize={queryParams.pageSize}
        total={accountsResponse?.['@odata.count']}
        nextLink={accountsResponse?.['@odata.nextLink']}
        totalRecordCountLimitExceeded={accountsResponse?.['@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded']}
        handleChange={handleTableChange}
      />

      {/* VS Setup Form Modal */}
      <Form
        form={form}
        layout="vertical"
        onFinish={isEditMode ? handleUpdateOnboarding : handleVSSetupSubmit}
      >
        <VSSetupFormModal
          isLoading={createAccountMutation.isPending || updateContactMutation.isPending}
          open={showVSSetupForm}
          isEditMode={isEditMode}
          onCancel={handleCancelForm}
          isRegionAll={isRegionAll}
          userRegion={userRegion}
        />
      </Form>

      {/* Send Reminder Confirmation Modal */}
      <Modal
        title="Send Reminder"
        open={showReminderModal}
        onCancel={() => {
          if (!sendReminderMutation.isPending) {
            setShowReminderModal(false);
            setReminderAccount(null);
          }
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowReminderModal(false);
              setReminderAccount(null);
            }}
            disabled={sendReminderMutation.isPending}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={sendReminderMutation.isPending}
            onClick={handleConfirmReminder}
          >
            Send Reminder
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to send a reminder to{' '}
          <strong>{reminderAccount?.primarycontactid?.fullname || reminderAccount?.name}</strong>?
        </p>
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal
        title="Delete Account"
        open={showDeactivateModal}
        onCancel={() => {
          if (!deactivateAccountMutation.isPending) {
            setShowDeactivateModal(false);
            setDeactivateAccount(null);
          }
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowDeactivateModal(false);
              setDeactivateAccount(null);
            }}
            disabled={deactivateAccountMutation.isPending}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            danger
            type="primary"
            loading={deactivateAccountMutation.isPending}
            onClick={handleConfirmDeactivate}
          >
            Delete
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to delete{' '}
          <strong>{deactivateAccount?.name}</strong>? This will prevent the
          account from accessing the portal.
        </p>
      </Modal>

      {/* Activate Account Confirmation Modal */}
      <Modal
        title="Activate Account"
        open={showActivateModal}
        onCancel={() => {
          if (!activateAccountMutation.isPending) {
            setShowActivateModal(false);
            setActivateAccount(null);
          }
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowActivateModal(false);
              setActivateAccount(null);
            }}
            disabled={activateAccountMutation.isPending}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={activateAccountMutation.isPending}
            onClick={handleConfirmActivate}
          >
            Activate
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to activate{' '}
          <strong>{activateAccount?.name}</strong>? This will restore the
          account's access to the portal.
        </p>
      </Modal>

      {/* Welcome Email Preview Modal */}
      <WelcomeEmailModal
        open={showWelcomeEmail}
        record={selectedAccountId ? (accounts.find(a => a.accountid === selectedAccountId) ?? null) : null}
        onClose={() => {
          setShowWelcomeEmail(false);
          setSelectedAccountId(null);
        }}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={() => toast('Bulk import functionality coming soon')}
      />
    </div>
  );
};

export default OnboardingPage;
